import { ActivityIndicator, TextInput, Pressable, View } from 'react-native';
import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Text } from '@/components/Text';
import colors from '@/config/colors';

export interface TwoFaDialogRef {
  updateError: (err: 'invalid' | 'expired') => void;
  resetOTP: () => void;
}

interface TwoFaDialogProps {
  invalidCode?: boolean;
  expiredCode?: boolean;
  onSubmit: (OTP: string) => void;
  onPressSendCodeAgain: () => void;
}

export const TwoFaDialog = forwardRef<TwoFaDialogRef, TwoFaDialogProps>(
  (
    {
      invalidCode = false,
      expiredCode = false,
      onSubmit,
      onPressSendCodeAgain,
    }: TwoFaDialogProps,
    ref,
  ) => {
    const [OTP, setOTP] = useState('');
    const [errorState, setErrorState] = useState<
      'none' | 'invalid' | 'expired'
    >('none');
    const [textInputFocused, setTextInputFocused] = useState(false);
    //State to indicate that the code was resent
    const [codeResent, setCodeResent] = useState(false);
    const textInputRef = useRef<TextInput>(null);

    // On mount/update, initialize the error state from props
    useEffect(() => {
      if (invalidCode) {
        setErrorState('invalid');
      } else if (expiredCode) {
        setErrorState('expired');
      } else {
        setErrorState('none');
      }
    }, [invalidCode, expiredCode]);

    useImperativeHandle(ref, () => ({
      resetOTP: () => {
        setOTP('');
        setErrorState('none');
      },
      updateError: (err: 'invalid' | 'expired') => {
        setErrorState(err);
      },
    }));

    useEffect(() => {
      if (OTP.length !== 6) return;
      textInputRef.current?.blur();
      onSubmit(OTP);
    }, [OTP]);

    // Handler for resending the code
    const handleResendCode = () => {
      setCodeResent(true);
      setTimeout(() => {
        setCodeResent(false);
      }, 3000);
      onPressSendCodeAgain();
    };

    return (
      <View className="p-6">
        <Text className="text-3xl mb-6">Sicherheitsüberprüfung</Text>
        {errorState === 'expired' && (
          <Text className="text-xl mb-6 text-red-500 font-bold">
            Der eingegebene Code ist nicht mehr gültig. Bitte erstellen Sie
            einen neuen Token.
          </Text>
        )}
        {errorState === 'invalid' && (
          <Text className="text-xl mb-6 text-red-500 font-bold">
            Der eingegebene Code ist ungültig. Bitte versuchen Sie es erneut.
          </Text>
        )}
        {errorState === 'none' && (
          <Text className="text-xl mb-6">
            Aus Sicherheitsgründen haben wir Ihnen einen Code per E-Mail
            gesendet. Bitte geben Sie diesen Code hier ein, um sich anzumelden.
          </Text>
        )}

        <View className="items-center">
          <View className="flex-row gap-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <Pressable
                key={index}
                className="border-2 h-14 w-12 rounded-xl items-center justify-center"
                style={{
                  borderColor: textInputFocused ? colors.black : colors.twGrey,
                }}
                onPress={() => textInputRef.current?.focus()}
              >
                <Text className="text-center text-2xl font-bold">
                  {OTP.split('')[index] ?? ''}
                </Text>
              </Pressable>
            ))}
            {OTP.length === 6 && (
              <View className="absolute -right-12 top-4">
                <ActivityIndicator color={colors.black} size={26} />
              </View>
            )}
          </View>
          <View>
            <Pressable onPress={handleResendCode}>
              <Text
                className={`text-lg mt-3 font-bold ${
                  codeResent ? 'text-indigo-900' : 'text-green-neolexon'
                }`}
              >
                Code erneut anfordern
              </Text>
            </Pressable>
          </View>
          {codeResent && (
            <Text className="mt-2 text-lg text-indigo-900">
              Ein neuer Code wurde an Ihre E-Mail-Adresse gesendet.
            </Text>
          )}
        </View>

        <TextInput
          onFocus={() => setTextInputFocused(true)}
          onBlur={() => setTextInputFocused(false)}
          ref={textInputRef}
          className="opacity-0"
          keyboardType="number-pad"
          maxLength={6}
          value={OTP}
          onChangeText={(text) => setOTP(text.replace(/[^0-9]/g, ''))}
        />
      </View>
    );
  },
);
